import React from "react";
import { toastr } from "react-redux-toastr";
import { matchesBoolean, matchesString } from "LIB/filters";
import { SUCCESS } from "LIB/submission";
import api from "APP_ROOT/api";
import { API_BASE } from "APP_ROOT/constants";
import { stringify } from "query-string";
import {
  FilterSelect,
  Pagination,
  SearchFilter,
  TitleWithFilters
} from "MODULES/shared/components/Pagination";
import { Search, Authorized, Button } from "MODULES/shared/components";
import OpenPayAll from "MODULES/Commissions/components/PayAll/OpenPayAll";
import DateRangePicker from "MODULES/shared/components/DateRangePicker/DateRangePicker";
import { renderIf } from "MODULES/shared/decorators";
import useCurrentUser from "APP_ROOT/hooks/useCurrentUser";
import { hasRole } from "MODULES/users/helpers";
import {
  ACCOUNTING_MANAGER,
  ADMIN,
  EXPORT_LIMIT
} from "MODULES/users/constants";
import Table from "./Table";
import pagination from "../../pagination";

const defaultFilters = {
  current_user: undefined,
  status: undefined,
  depreciation_only: undefined,
  mro_only: undefined,
  customer_paid: undefined,
  sales_order_only: undefined,
  freight_not_complete: undefined
};

const filters = [
  {
    label: "Show All",
    filters: { ...defaultFilters }
  },
  {
    label: "Show Comm. Paid",
    filters: { status: "paid" }
  },
  {
    label: "Show Comm. Pending",
    filters: { status: "pending,reversed" }
  },
  {
    label: "Show All Bad Debt",
    filters: { status: "bad_debt" }
  },
  {
    label: "Show Depreciation Only",
    filters: {
      sales_order_only: undefined,
      mro_only: undefined,
      depreciation_only: true
    }
  },
  {
    label: "No Depreciations",
    filters: {
      sales_order_only: undefined,
      depreciation_only: false
    }
  },
  {
    label: "Show MRs Only",
    filters: {
      sales_order_only: undefined,
      depreciation_only: undefined,
      mro_only: true
    }
  },
  {
    label: "Show SOs Only",
    filters: { depreciation_only: undefined, sales_order_only: true }
  },
  {
    label: "Show Customer Paid",
    filters: { customer_paid: true }
  },
  {
    label: "Show Customer Payment Pending",
    filters: { customer_paid: false, depreciation_only: undefined }
  },
  {
    label: "Show Freight Not Complete",
    filters: { freight_not_complete: true }
  }
];

const filterTags = [
  {
    label: "Commission Paid",
    key: "status",
    matchesTarget: matchesString("paid")
  },
  {
    label: "Commission Pending",
    key: "status",
    matchesTarget: matchesString("pending,reversed")
  },
  {
    label: "Commission Bad Debt",
    key: "status",
    matchesTarget: matchesString("bad_debt")
  },
  {
    label: "Depreciation Only",
    key: "depreciation_only",
    matchesTarget: matchesBoolean(true)
  },
  {
    label: "No Depreciations",
    key: "depreciation_only",
    matchesTarget: matchesBoolean(false)
  },
  {
    label: "Sales Order Only",
    key: "sales_order_only"
  },
  {
    label: "Customer Paid",
    key: "customer_paid",
    matchesTarget: matchesBoolean(true)
  },
  {
    label: "Customer Payment Pending",
    key: "customer_paid",
    matchesTarget: matchesBoolean(false)
  },
  {
    label: "Managed Repairs Only",
    key: "mro_only",
    matchesTarget: matchesBoolean(true)
  },
  {
    label: "Show Freight Not Complete",
    key: "freight_not_complete",
    matchesTarget: matchesBoolean(true)
  }
];

const ExportButton = ({ searchProps }) => {
  const params = searchProps.paginator.get("filters").toJS();
  const totalCount = searchProps.paginator.get("totalCount");

  params.total_count = totalCount;
  return (
    <Button
      onClick={() => {
        if (totalCount > EXPORT_LIMIT) {
          toastr.info("Processing Report.");
          api.commissions.export(params).then(() => SUCCESS);
        } else {
          window.open(
            `${API_BASE}/commissions/export?${stringify(params)}`,
            "_blank"
          );
          toastr.success(
            "Processing Commissions Report. Please wait for the PDF to generate."
          );
        }
      }}
    >
      Export
    </Button>
  );
};

const meetsExportCriteria = renderIf(({ searchProps }) => {
  const user = useCurrentUser();
  return searchProps.paginator.get("totalCount") <= 450 || hasRole(user, ADMIN);
});

const DecoratedExportButton = meetsExportCriteria(ExportButton);

export default function CommissionsSearch() {
  return (
    <Search search={pagination} syncWithUrl>
      {searchProps => (
        <div>
          <div className="form-group form-group-searchbar has-feedback has-feedback-left">
            <i className="text-muted fa fa-search form-control-feedback form-control-feedback-left" />
            <div className="input-group">
              <SearchFilter
                {...searchProps}
                placeholder="Search by typing any ref number, company name, seller name, serial #, part #, or commission paid date..."
              />
              <DecoratedExportButton searchProps={searchProps} />
              <DateRangePicker {...searchProps} />
              <FilterSelect {...searchProps} filters={filters} right />
              <Authorized subroles={ACCOUNTING_MANAGER}>
                <OpenPayAll />
              </Authorized>
            </div>
          </div>
          <TitleWithFilters
            {...searchProps}
            title="Commission Report"
            filters={filterTags}
            aggregateField="total_commission"
          />
          <Table {...searchProps} />
          <Pagination {...searchProps} />
        </div>
      )}
    </Search>
  );
}



// WEBPACK FOOTER //
// ./app/modules/Commissions/components/Search/Search.jsx